import { apiC } from "@services/api/apiC";
import { ActionPlan, ActionPlanType, AddActionPlanTargetBody, AddActionPlanTargetCommentBody, ChangeActionPlanLevelBody, ChangeActionPlanTargetStatusBody, EditActionPlanTargetBody, GetActionPlanDropdownValuesSuccess, MarkActionPlanReferralReadBody, MarkActionPlanReferralReadParams, ReferActionPlanBody } from "@godalming/api-types";
import { usePortalModeStore } from "@stores/usePortalModeStore";
import { useStaffOverrideStore } from "@stores/useStaffOverrideStore";

export async function fetchStudentActionPlans(studentNo: string) {
    // Temporary: api.godalming.ac.uk only accepts staff authentication. Returning
    // an empty result prevents student/parent routes and preloaders from issuing a
    // request that is guaranteed to fail with 401/403.
    const { viewMode } = usePortalModeStore.getState();
    const { override, overrideType } = useStaffOverrideStore.getState();
    const impersonatingStudent = overrideType === 'student' && Boolean(override);
    if (viewMode !== 'staff' || impersonatingStudent) {
        return [];
    }

    try {
        const response = await apiC.get<ActionPlan[]>(`/StudentAcademicSupportPlanList`, {
            params: {
                studentNo: studentNo
            }
        });
        const data = response.data.map(plan => ({
            ...plan,
            type: plan.type as any === 'academic-support-plan' ? 'academic-support' : plan.type
        }))
        return data
    } catch (error) {
        console.error("Error fetching student action plans:", error);
        throw error;
    }
}

export async function createActionPlanTarget(params: AddActionPlanTargetBody) {
    try {
        const response = await apiC.post('/StudentActionPlanTargetAdd', params);
        if (response.data.errorMessage) {
            console.error("Error creating action plan target:", response.data.errorMessage);
            throw new Error(response.data.errorMessage);
        }
        return response.data;
    } catch (error) {
        console.error("Error creating action plan target:", error);
        throw error;
    }
}

export async function changeActionPlanTargetStatus(params: ChangeActionPlanTargetStatusBody) {
    try {
        const response = await apiC.post('/StudentActionPlanTargetChangeStatus', params);
        if (response.data.errorMessage) {
            console.error("Error changing action plan target status:", response.data.errorMessage);
            throw new Error(response.data.errorMessage);
        }
        return response.data;
    } catch (error) {
        console.error("Error changing action plan target status:", error);
        throw error;
    }
}

export async function editActionPlanTarget(params: EditActionPlanTargetBody) {
    try {
        const response = await apiC.post('/StudentActionPlanTargetEdit', params);
        if (response.data.errorMessage) {
            console.error("Error editing action plan target:", response.data.errorMessage);
            throw new Error(response.data.errorMessage);
        }
        return response.data;
    } catch (error) {
        console.error("Error editing action plan target:", error);
        throw error;
    }
}

export async function createActionPlanTargetComment(params: AddActionPlanTargetCommentBody) {
    try {
        const response = await apiC.post('/StudentActionPlanTargetCommentAdd', params);
        if (response.data.errorMessage) {
            console.error("Error creating action plan target comment:", response.data.errorMessage);
            throw new Error(response.data.errorMessage);
        }
        return response.data;
    } catch (error) {
        console.error("Error creating action plan target comment:", error);
        throw error;
    }
}

export async function referActionPlan(params: ReferActionPlanBody) {
    try {
        const response = await apiC.post('/StudentActionPlanRefer', params);
        // API returns 200 OK even when there's an error, so we need to check the response body for error messages
        if (response.data.errorMessage) {
            console.error("Error referring action plan:", response.data.errorMessage);
            throw new Error(response.data.errorMessage);
        }
        return response.data;
    } catch (error) {
        console.error("Error referring action plan:", error);
        throw error;
    }
}

export async function markReferralAsRead(params: MarkActionPlanReferralReadBody) {
    try {
        const response = await apiC.post('/StudentActionPlanMarkReferRead', params);
        if (response.data.errorMessage) {
            console.error("Error marking referral as read:", response.data.errorMessage);
            throw new Error(response.data.errorMessage);
        }
        return response.data;
    } catch (error) {
        console.error("Error marking referral as read:", error);
        throw error;
    }
}

export async function markReferralAsUnread(params: MarkActionPlanReferralReadBody) {
    params.readNote = undefined
    try {
        const response = await apiC.post('/StudentActionPlanMarkReferRead', params);
        if (response.data.errorMessage) {
            console.error("Error marking referral as unread:", response.data.errorMessage);
            throw new Error(response.data.errorMessage);
        }
        return response.data;
    } catch (error) {
        console.error("Error marking referral as unread:", error);
        throw error;
    }
}

export async function changeActionPlanLevel(params: ChangeActionPlanLevelBody) {
    try {
        const response = await apiC.post('/StudentActionPlanChangeLevel', params);
        if (response.data.errorMessage) {
            console.error("Error changing action plan level:", response.data.errorMessage);
            throw new Error(response.data.errorMessage);
        }
        return response.data;
    } catch (error) {
        console.error("Error changing action plan level:", error);
        throw error;
    }
}

export async function getActionPlanDropdownValues() {
    try {
        const response = await apiC.get<GetActionPlanDropdownValuesSuccess>('/StudentActionPlanTypeDetail')
        return response.data;
    } catch (error) {
        console.error("Error fetching action plan dropdown values:", error);
        throw error;
    }
}

type ActionPlanManagerRow = {
    studentNo: string,
    displayName: string,
    surname: string,
    studentActionPlanList: ActionPlan[]
}

export async function fetchActionPlanManagerActionPlans() {
    const { viewMode } = usePortalModeStore.getState();
    const { override, overrideType } = useStaffOverrideStore.getState();
    const impersonatingStudent = overrideType === 'student' && Boolean(override);
    if (viewMode !== 'staff' || impersonatingStudent) {
        return [];
    }

    try {
        const response = await apiC.get<ActionPlanManagerRow[]>(`/StudentActionPlanManager`);
        console.log(response.data)
        return response.data
    } catch (error) {
        console.error("Error fetching student action plans:", error);
        throw error;
    }
}